import "@fontsource-variable/inter";
import { Badge, createTheme, type CSSVariablesResolver } from "@mantine/core";
import { PANEL_BORDER, PANEL_COLOR, PANEL_FONT, PANEL_PALETTES, PANEL_RADIUS, PANEL_SPACE } from "./tokens";

/**
 * The panel's Mantine theme, built from its own tokens (./tokens.ts). Component defaults
 * carry the Figma look so pages compose plain Mantine inputs and buttons: 13px Inter, 3px
 * inputs on the `border/default` line, labels in regular weight 10px above the input, 8px
 * buttons, and modals that read as a card that floats (a gray header band over a white body).
 */
export const theme = createTheme({
  primaryColor: "blue",
  primaryShade: 7,
  colors: PANEL_PALETTES,
  fontFamily: PANEL_FONT.family,
  fontSizes: { xs: PANEL_FONT.small, sm: PANEL_FONT.body, md: PANEL_FONT.body, lg: "16px", xl: "20px" },
  headings: { fontFamily: PANEL_FONT.family, fontWeight: "600" },
  defaultRadius: PANEL_RADIUS.input,
  components: {
    Button: {
      defaultProps: { radius: PANEL_RADIUS.button, size: "sm" },
      styles: { label: { fontWeight: 500 } },
    },
    InputWrapper: {
      styles: { label: { fontWeight: 400, fontSize: PANEL_FONT.body, marginBottom: PANEL_SPACE.field } },
    },
    Input: { defaultProps: { radius: PANEL_RADIUS.input } },
    Switch: { defaultProps: { size: "sm", withThumbIndicator: false } },
    NumberInput: { defaultProps: { hideControls: true } },
    Modal: {
      defaultProps: {
        radius: PANEL_RADIUS.card,
        centered: true,
        overlayProps: { color: PANEL_COLOR.modalOverlay, backgroundOpacity: 0.65, blur: 2 },
      },
      styles: {
        header: {
          background: PANEL_COLOR.fill,
          borderBottom: PANEL_BORDER,
          minHeight: 40,
          paddingBlock: 0,
          paddingInline: PANEL_SPACE.card,
        },
        title: { fontSize: PANEL_FONT.body, fontWeight: 400, color: PANEL_COLOR.headerText },
        body: { padding: PANEL_SPACE.card },
      },
    },
    // The add-in's badge recipe (.claude/rules/ui.md): body size at medium
    // weight, 1.25 line-height, 7px x 3px padding, 6px corners, no uppercase; the light
    // variant (the default) is a borderless soft rect in the tone's [1] fill with its [7]
    // text, and outline is white on the `border/default` line with the same text. Other
    // variants keep Mantine's colors. The tone is the `color` prop (blue when unset or not a
    // panel palette).
    Badge: Badge.extend({
      defaultProps: { variant: "light", radius: 6 },
      vars: (_theme, props) => {
        const tone = PANEL_PALETTES[(props.color as keyof typeof PANEL_PALETTES) ?? "blue"] ?? PANEL_PALETTES.blue;
        const variant = props.variant ?? "light";
        const colors =
          variant === "light"
            ? { "--badge-bg": tone[1], "--badge-color": tone[7], "--badge-bd": "none" }
            : variant === "outline"
              ? { "--badge-bg": "#fff", "--badge-color": tone[7], "--badge-bd": PANEL_BORDER }
              : {};
        return {
          root: {
            ...colors,
            "--badge-fz": PANEL_FONT.body,
            "--badge-height": "auto",
            "--badge-padding-x": "7px",
          },
        };
      },
      styles: { root: { textTransform: "none", fontWeight: 500, lineHeight: 1.25, paddingBlock: 3 } },
    }),
    Anchor: { defaultProps: { fw: 500 } },
    Loader: { defaultProps: { size: "sm" } },
  },
});

/** The shared lines (inputs, tables, dividers) are `border/default`; dimmed text is
 * `text/secondary`. */
export const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {},
  light: {
    "--mantine-color-default-border": PANEL_COLOR.border,
    "--mantine-color-dimmed": PANEL_COLOR.textSecondary,
    "--mantine-color-text": PANEL_COLOR.textPrimary,
  },
  dark: {},
});
